import React from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { AlertTriangle, Check, Copy, Search } from "react-feather";
import { useGet, usePost } from "../../api";
import { IDuplicateGroupsResponse } from "../../interfaces";

const PAGE_SIZE = 25;

/**
 * The same shop in the same market recorded more than once — usually a www and
 * a non-www row. Both get scheduled, so both run every day.
 */
export const MerchantDuplicates: React.FC = () => {
  const [offset, setOffset] = React.useState(0);
  const [domain, setDomain] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [chosen, setChosen] = React.useState<Record<string, string>>({});
  const [merged, setMerged] = React.useState<Record<string, number>>({});
  const [busy, setBusy] = React.useState<string | null>(null);

  const url = `/merchants/duplicates?offset=${offset}&limit=${PAGE_SIZE}${
    search ? `&domain=${encodeURIComponent(search)}` : ""
  }`;
  const { data, loading, refetch } = useGet<IDuplicateGroupsResponse>(url);
  const mergePost = usePost<{ merged: number }, any>();

  const mergeGroup = async (key: string, keepId: string, allIds: string[]) => {
    setBusy(key);
    try {
      const res = await mergePost.execute("/merchants/duplicates/merge", {
        keepId,
        removeIds: allIds.filter((id) => id !== keepId),
      });
      setMerged((prev) => ({ ...prev, [key]: res?.merged ?? 0 }));
    } catch {
      window.alert("Could not merge that group.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <Container fluid className="py-4">
      <Row className="mb-3">
        <Col>
          <h2 className="mb-0">
            <Copy className="me-2" />
            Duplicate merchants
          </h2>
          <small className="text-muted">
            The same shop and market recorded twice. Both rows are scheduled, so
            both run every day — merging keeps one and stops the other.
          </small>
        </Col>
      </Row>

      {data && (
        <Alert variant={data.totals.doubleScheduled ? "warning" : "success"}>
          <AlertTriangle size={16} className="me-2" />
          <strong>{data.totals.groups}</strong> duplicated{" "}
          {data.totals.groups === 1 ? "group" : "groups"},{" "}
          <strong>{data.totals.redundantRows}</strong> redundant{" "}
          {data.totals.redundantRows === 1 ? "row" : "rows"}, of which{" "}
          <strong>{data.totals.doubleScheduled}</strong> are running more than
          once a day.
        </Alert>
      )}

      <Row className="mb-3">
        <Col md={5}>
          <InputGroup>
            <Form.Control
              placeholder="Filter by domain"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setOffset(0);
                  setSearch(domain);
                }
              }}
            />
            <Button
              variant="outline-secondary"
              onClick={() => {
                setOffset(0);
                setSearch(domain);
              }}
            >
              <Search size={16} />
            </Button>
          </InputGroup>
        </Col>
      </Row>

      {loading && (
        <div className="text-center py-5">
          <Spinner />
        </div>
      )}

      {!loading &&
        (data?.items ?? []).map((g) => {
          const keepId = chosen[g.key] ?? g.candidates.find((c) => c.suggested)?._id;
          const done = merged[g.key] !== undefined;
          return (
            <Card key={g.key} className="mb-3">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <div>
                  <strong>{g.domain}</strong>{" "}
                  <Badge bg="light" text="dark">
                    {g.countryCode}
                  </Badge>{" "}
                  <span className="text-muted small">
                    {g.candidates.length} rows
                  </span>
                  {g.scheduledCount > 1 && (
                    <Badge bg="warning" text="dark" className="ms-2">
                      running {g.scheduledCount}× a day
                    </Badge>
                  )}
                </div>
                {done ? (
                  <span className="text-success">
                    <Check size={16} className="me-1" />
                    {merged[g.key]} merged
                  </span>
                ) : (
                  <Button
                    size="sm"
                    variant="primary"
                    disabled={!keepId || busy === g.key}
                    onClick={() =>
                      mergeGroup(
                        g.key,
                        keepId!,
                        g.candidates.map((c) => c._id)
                      )
                    }
                  >
                    {busy === g.key
                      ? "Merging…"
                      : `Merge ${g.candidates.length - 1} into selected`}
                  </Button>
                )}
              </Card.Header>
              <Card.Body className="p-0">
                <Table hover responsive className="mb-0 align-middle">
                  <thead>
                    <tr>
                      <th style={{ width: 90 }}>Keep</th>
                      <th>URL</th>
                      <th>Checkout</th>
                      <th>Has</th>
                      <th>Scheduled</th>
                      <th className="text-end">Coupon results</th>
                      <th>Last run</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.candidates.map((c) => (
                      <tr
                        key={c._id}
                        className={keepId === c._id ? "table-primary" : undefined}
                      >
                        <td>
                          <Form.Check
                            type="radio"
                            name={`keep-${g.key}`}
                            disabled={done}
                            checked={keepId === c._id}
                            onChange={() =>
                              setChosen((prev) => ({ ...prev, [g.key]: c._id }))
                            }
                            label={c.suggested ? "suggested" : ""}
                          />
                        </td>
                        <td className="small">{c.homePageUrl}</td>
                        <td className="small text-muted">{c.checkoutType ?? "—"}</td>
                        <td>
                          {c.hasSession && (
                            <Badge bg="success" className="me-1">
                              session
                            </Badge>
                          )}
                          {c.hasApiCredentials && (
                            <Badge bg="secondary">credentials</Badge>
                          )}
                          {!c.hasSession && !c.hasApiCredentials && (
                            <span className="text-muted small">—</span>
                          )}
                        </td>
                        <td>
                          {c.scheduleEnabled ? (
                            <Badge bg="primary">on</Badge>
                          ) : (
                            <span className="text-muted small">off</span>
                          )}
                        </td>
                        <td className="text-end small">{c.couponResults}</td>
                        <td className="small text-muted">
                          {c.lastRunAt
                            ? new Date(c.lastRunAt).toISOString().slice(0, 10)
                            : "never"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          );
        })}

      {!loading && !(data?.items ?? []).length && (
        <Card>
          <Card.Body className="text-center text-muted py-5">
            No duplicates found.
          </Card.Body>
        </Card>
      )}

      {!loading && (data?.total ?? 0) > PAGE_SIZE && (
        <div className="d-flex justify-content-between align-items-center">
          <Button
            variant="outline-secondary"
            size="sm"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          >
            Previous
          </Button>
          <span className="text-muted small">
            {offset + 1}–{Math.min(offset + PAGE_SIZE, data?.total ?? 0)} of{" "}
            {data?.total}
          </span>
          <Button
            variant="outline-secondary"
            size="sm"
            disabled={offset + PAGE_SIZE >= (data?.total ?? 0)}
            onClick={() => {
              setOffset(offset + PAGE_SIZE);
              refetch?.();
            }}
          >
            Next
          </Button>
        </div>
      )}
    </Container>
  );
};
